
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Clock, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardData } from '@/hooks/use-dashboard-data';

interface WorkoutListProps {
  isLoading: boolean;
  workouts: DashboardData['upcomingWorkouts'];
}

export const WorkoutList = ({ isLoading, workouts }: WorkoutListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3 rounded-lg animate-pulse bg-secondary/20" style={{ height: "60px" }} />
        ))}
      </div>
    );
  }

  if (workouts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
        <Target className="h-12 w-12 mb-4 opacity-20" />
        <p>No upcoming workouts</p>
        <p className="text-sm mt-1">Start your AI training journey today</p>
      </div>
    );
  }

  return (
    <ul className="space-y-4">
      {workouts.map((workout) => (
        <motion.li
          key={workout.id}
          whileHover={{ x: 5 }}
          className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">{workout.title}</p>
              <p className="text-sm text-muted-foreground">{workout.duration}</p>
            </div>
          </div>
          <div className="flex items-center">
            <span className="text-sm font-medium rounded-full bg-primary/10 px-3 py-1 text-primary">
              {workout.day}
            </span>
          </div>
        </motion.li>
      ))}
    </ul>
  );
};

export const WorkoutCard = ({ isLoading, workouts }: WorkoutListProps) => {
  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl font-lexend flex items-center">
          <Target className="h-5 w-5 mr-2 text-primary" />
          Upcoming Workouts
        </CardTitle>
        <CardDescription>Your AI training schedule</CardDescription>
      </CardHeader>
      <CardContent>
        <WorkoutList isLoading={isLoading} workouts={workouts} />
      </CardContent>
      <CardFooter>
        <Button variant="ghost" asChild className="w-full">
          <Link to="/schedule" className="flex items-center justify-center">
            <Plus className="h-4 w-4 mr-2" />
            View Full Schedule
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};
